import { Component, Input, OnChanges, OnInit, SimpleChanges, forwardRef } from '@angular/core';

import { of } from 'rxjs';

import { AbstractFormFieldComponent } from '../../../../PageBuilder/components/FormField/abstract-form-field.component';
import { DropdownFieldModel } from '../../../../PageBuilder/models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { ChartDisplayElementModel } from '../../../models/chart-display-element.model';
import { SeriesTypeEnum } from '../../../enums/series-type.enum';
import { ReportSeriesMentionsModel } from '../../../models/report-series-mentions.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { ConfirmCancelDialogComponent } from '../../../../Dialogs/components/ConfirmCancelDialog/confirm-cancel-dialog.component';


@Component({
    selector: 'agpr-top-mentions-sort-field',
    templateUrl: './top-mentions-sort-field.component.html',
    styleUrls: ['./top-mentions-sort-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => TopMentionsSortFieldComponent)
        }
    ],
    standalone: false
})
export class TopMentionsSortFieldComponent extends AbstractFormFieldComponent implements OnInit, OnChanges {

    @Input() declare field: DropdownFieldModel;

    options: DropdownOptionModel[] = [];
    selectedOption: DropdownOptionModel;
    showMessage: boolean = false;
    ariaLabel: string;
    oldMentionSeries: ReportSeriesMentionsModel;

    readonly messageSeverities = messageSeverityEnum;
    readonly unsavedSeriesTypes = [
        SeriesTypeEnum.topic,
        SeriesTypeEnum.subscription,
        SeriesTypeEnum.theme,
        SeriesTypeEnum.socialTopic
    ];

    constructor(
        private coreDialogService: CoreDialogService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.options = this.field.options.slice();
        this.setSelectedOption();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.value || this.options.length === 0) {
            return;
        }

        this.options = this.field.options.slice();
        const series = (this.block.displayElement as ChartDisplayElementModel)?.series?.[0];

        if (this.unsavedSeriesTypes.includes(series.type)) {
            this.options = this.options.filter(x => x.value !== SearchCriteriaSortFieldEnum.relevancy);
        }

        this.setSelectedOption();
    }

    onDropdownChanged(value: any) {
        if ((this.value === SearchCriteriaSortFieldEnum.custom) && (value !== SearchCriteriaSortFieldEnum.custom)) {
            this.coreDialogService.open(ConfirmCancelDialogComponent, {
                inputs: {
                    id: 'reports_topPosts_analysis_change_confirm_dialog',
                    heading: 'Reports.topPosts.analysis.change.confirmHeading',
                    alertMessage: 'Reports.topPosts.analysis.change.confirmAlertMessage',
                    alertType: messageSeverityEnum.warning,
                    okLabel: 'Reports.topPosts.analysis.change.confirmConfirm',
                    confirmAction: () => {
                        this.handleDropdownChanged(value);
                        return of(true);
                    },
                    cancelAction: () => {
                        this.setSelectedOption();
                        return of(true);
                    }
                }
            });

        } else {
            this.handleDropdownChanged(value);
        }
    }

    handleDropdownChanged(value: any) {
        this.value = value;
        this.showMessage = this.value === SearchCriteriaSortFieldEnum.custom;
        const series = (this.block.displayElement as ChartDisplayElementModel).series;
        if (series?.length) {
            series.forEach(s => s.sortField = this.value);
        }

        if (this.value === SearchCriteriaSortFieldEnum.custom) {
            this.oldMentionSeries = (this.block.displayElement as ChartDisplayElementModel).series[0].mentions;
            (this.block.displayElement as ChartDisplayElementModel).series[0].mentions = {};
        } else if (this.oldMentionSeries) {
            (this.block.displayElement as ChartDisplayElementModel).series[0].mentions = this.oldMentionSeries;
            this.oldMentionSeries = null;
        }

        this.changes.emit(this.value);
    }

    setSelectedOption(): void {
        if (this.options?.length > 0) {
            this.selectedOption = this.options.find(option => option.value === this.value);
            if (!this.selectedOption) {
                this.selectedOption = this.options[0];
                this.onDropdownChanged(this.selectedOption.value);
            }

            this.ariaLabel = this.field.ariaLabel;
            if (!this.ariaLabel) {
                this.ariaLabel = this.selectedOption.label;
            }
            this.showMessage = this.value === SearchCriteriaSortFieldEnum.custom;
        }
    }
}
